/**
 * useWebSocket hook for real-time WebSocket events.
 *
 * This hook provides a React-friendly interface for the Socket.IO
 * connection to receive real-time events from the web-backend.
 *
 * Features:
 * - Listen for events: message, status, replace, source, citation
 * - Listen for file-processing events
 * - Dispatch events to appropriate callbacks or stores
 * - Implement heartbeat (every 30s)
 * - Handle reconnection
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  acquireConnection,
  connect,
  disconnect,
  isConnected,
  getConnectionState,
  subscribeToEvent,
  onConnectionEvent,
  sendHeartbeat,
  cleanupSocket,
} from '@/lib/socket';
import type {
  ConnectionState,
  MessageEvent,
  StatusEvent,
  ReplaceEvent,
  SourceEvent,
  CitationEvent,
  FileProcessingEvent,
  KnowledgeDocumentProcessingEvent,
  ChatTitleUpdatedEvent,
  ChatCreatedEvent,
  ChatProgressEvent,
  AvatarUpdatedEvent,
} from '@/types/websocket';

/**
 * Heartbeat interval in milliseconds (30 seconds).
 */
const HEARTBEAT_INTERVAL = 30000;

/**
 * Event callbacks for the useWebSocket hook.
 */
export interface WebSocketEventCallbacks {
  /**
   * Called when a message content chunk is received.
   */
  onMessage?: (data: MessageEvent) => void;

  /**
   * Called when a status update is received.
   */
  onStatus?: (data: StatusEvent) => void;

  /**
   * Called when a content replacement is received.
   */
  onReplace?: (data: ReplaceEvent) => void;

  /**
   * Called when a source reference is received.
   */
  onSource?: (data: SourceEvent) => void;

  /**
   * Called when a citation is received.
   */
  onCitation?: (data: CitationEvent) => void;

  /**
   * Called when a file processing status update is received.
   */
  onFileProcessing?: (data: FileProcessingEvent) => void;

  /**
   * Called when a knowledge document processing status update is received.
   */
  onKnowledgeProcessing?: (data: KnowledgeDocumentProcessingEvent) => void;

  /**
   * Called when a chat title is updated by the backend.
   */
  onChatTitleUpdated?: (data: ChatTitleUpdatedEvent) => void;

  /**
   * Called when a new chat is created by the backend (e.g. voice sessions).
   */
  onChatCreated?: (data: ChatCreatedEvent) => void;

  /**
   * Called when the backend relays a per-chat streaming progress event.
   * Used to keep progress UI updating on PWA wake when the original SSE
   * connection has been killed by the OS.
   */
  onChatProgress?: (data: ChatProgressEvent) => void;

  /**
   * Called when the user's Andi look changed (edit succeeded or reset). The
   * payload is only a hint and may be malformed; treat it as "refetch".
   */
  onAvatarUpdated?: (data: AvatarUpdatedEvent) => void;
}

/**
 * Options for the useWebSocket hook.
 */
export interface UseWebSocketOptions extends WebSocketEventCallbacks {
  /**
   * Whether to automatically connect on mount.
   * Default: true
   */
  autoConnect?: boolean;

  /**
   * Whether to enable heartbeat.
   * Default: true
   */
  enableHeartbeat?: boolean;

  /**
   * Callback when connection is established.
   */
  onConnect?: () => void;

  /**
   * Callback when connection is lost.
   */
  onDisconnect?: (reason: string) => void;

  /**
   * Callback when a connection error occurs.
   */
  onError?: (error: Error) => void;

  /**
   * Callback when reconnection attempt starts.
   */
  onReconnectAttempt?: (attempt: number) => void;

  /**
   * Callback when reconnection succeeds.
   */
  onReconnect?: (attempt: number) => void;

  /**
   * Callback when reconnection fails.
   */
  onReconnectFailed?: () => void;
}

/**
 * Return type for the useWebSocket hook.
 */
export interface UseWebSocketReturn {
  /**
   * Current connection state.
   */
  connectionState: ConnectionState;

  /**
   * Whether the socket is currently connected.
   */
  isConnected: boolean;

  /**
   * Connect to the WebSocket server.
   */
  connect: () => Promise<void>;

  /**
   * Disconnect from the WebSocket server.
   */
  disconnect: () => void;

  /**
   * Error that occurred during connection (if any).
   */
  error: Error | null;
}

/**
 * Hook for managing WebSocket connection and receiving real-time events.
 *
 * @param options - Hook configuration options
 * @returns WebSocket connection state and control functions
 *
 * @example
 * ```tsx
 * function ChatComponent() {
 *   const [sources, setSources] = useState<SourceEvent[]>([]);
 *
 *   const { connectionState, isConnected, connect } = useWebSocket({
 *     onMessage: (data) => {
 *       // Handle incoming message chunks
 *       console.log('Message chunk:', data.content);
 *     },
 *     onStatus: (data) => {
 *       // Update UI based on status
 *       console.log('Status:', data.status);
 *     },
 *     onSource: (data) => {
 *       // Collect source references
 *       setSources(prev => [...prev, data]);
 *     },
 *     onFileProcessing: (data) => {
 *       // Update file status in file list
 *       console.log('File status:', data.file_id, data.status);
 *     },
 *     onConnect: () => {
 *       console.log('Connected!');
 *     },
 *     onDisconnect: (reason) => {
 *       console.log('Disconnected:', reason);
 *     },
 *   });
 *
 *   return (
 *     <div>
 *       <div>Status: {connectionState}</div>
 *       {!isConnected && (
 *         <button onClick={connect}>Connect</button>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const {
    autoConnect = true,
    enableHeartbeat = true,
    onMessage,
    onStatus,
    onReplace,
    onSource,
    onCitation,
    onFileProcessing,
    onKnowledgeProcessing,
    onChatTitleUpdated,
    onChatCreated,
    onChatProgress,
    onAvatarUpdated,
    onConnect: onConnectCallback,
    onDisconnect: onDisconnectCallback,
    onError: onErrorCallback,
    onReconnectAttempt: onReconnectAttemptCallback,
    onReconnect: onReconnectCallback,
    onReconnectFailed: onReconnectFailedCallback,
  } = options;

  // State
  const [connectionState, setConnectionState] = useState<ConnectionState>(getConnectionState);
  const [socketIsConnected, setSocketIsConnected] = useState(isConnected);
  const [error, setError] = useState<Error | null>(null);

  // Refs for callbacks (to avoid stale closures)
  const callbacksRef = useRef({
    onMessage,
    onStatus,
    onReplace,
    onSource,
    onCitation,
    onFileProcessing,
    onKnowledgeProcessing,
    onChatTitleUpdated,
    onChatCreated,
    onChatProgress,
    onAvatarUpdated,
    onConnectCallback,
    onDisconnectCallback,
    onErrorCallback,
    onReconnectAttemptCallback,
    onReconnectCallback,
    onReconnectFailedCallback,
  });

  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = {
      onMessage,
      onStatus,
      onReplace,
      onSource,
      onCitation,
      onFileProcessing,
      onKnowledgeProcessing,
      onChatTitleUpdated,
      onChatCreated,
      onChatProgress,
      onAvatarUpdated,
      onConnectCallback,
      onDisconnectCallback,
      onErrorCallback,
      onReconnectAttemptCallback,
      onReconnectCallback,
      onReconnectFailedCallback,
    };
  }, [
    onMessage,
    onStatus,
    onReplace,
    onSource,
    onCitation,
    onFileProcessing,
    onKnowledgeProcessing,
    onChatTitleUpdated,
    onChatCreated,
    onChatProgress,
    onAvatarUpdated,
    onConnectCallback,
    onDisconnectCallback,
    onErrorCallback,
    onReconnectAttemptCallback,
    onReconnectCallback,
    onReconnectFailedCallback,
  ]);

  // Heartbeat interval ref
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Start heartbeat interval.
   */
  const startHeartbeat = useCallback(() => {
    if (!enableHeartbeat) return;

    // Clear any existing interval
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    // Start new interval
    heartbeatIntervalRef.current = setInterval(() => {
      void sendHeartbeat();
    }, HEARTBEAT_INTERVAL);
  }, [enableHeartbeat]);

  /**
   * Stop heartbeat interval.
   */
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  /**
   * Connect to the WebSocket server.
   */
  const handleConnect = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      await connect();
    } catch (err) {
      const connectionError = err instanceof Error ? err : new Error(String(err));
      setError(connectionError);
      throw connectionError;
    }
  }, []);

  /**
   * Disconnect from the WebSocket server.
   */
  const handleDisconnect = useCallback((): void => {
    stopHeartbeat();
    disconnect();
  }, [stopHeartbeat]);

  // Set up connection event listeners
  useEffect(() => {
    const unsubscribers: Array<() => void> = [];

    // Connection state changes
    unsubscribers.push(
      onConnectionEvent('state', (data) => {
        const { state } = data as { state: ConnectionState };
        setConnectionState(state);
        setSocketIsConnected(state === 'connected');
      })
    );

    // Connect event
    unsubscribers.push(
      onConnectionEvent('connect', () => {
        setSocketIsConnected(true);
        setConnectionState('connected');
        setError(null);
        startHeartbeat();
        callbacksRef.current.onConnectCallback?.();
      })
    );

    // Disconnect event
    unsubscribers.push(
      onConnectionEvent('disconnect', (data) => {
        const { reason } = data as { reason: string };
        setSocketIsConnected(false);
        setConnectionState('disconnected');
        stopHeartbeat();
        callbacksRef.current.onDisconnectCallback?.(reason);
      })
    );

    // Error event
    unsubscribers.push(
      onConnectionEvent('error', (data) => {
        const { error: err } = data as { error: Error };
        setError(err);
        setConnectionState('error');
        callbacksRef.current.onErrorCallback?.(err);
      })
    );

    // Reconnect attempt event
    unsubscribers.push(
      onConnectionEvent('reconnect_attempt', (data) => {
        const { attempt } = data as { attempt: number };
        setConnectionState('reconnecting');
        callbacksRef.current.onReconnectAttemptCallback?.(attempt);
      })
    );

    // Reconnect success event
    unsubscribers.push(
      onConnectionEvent('reconnect', (data) => {
        const { attempt } = data as { attempt: number };
        setConnectionState('connected');
        setSocketIsConnected(true);
        startHeartbeat();
        callbacksRef.current.onReconnectCallback?.(attempt);
      })
    );

    // Reconnect failed event
    unsubscribers.push(
      onConnectionEvent('reconnect_failed', () => {
        setConnectionState('error');
        callbacksRef.current.onReconnectFailedCallback?.();
      })
    );

    return () => {
      unsubscribers.forEach((unsub) => {
        unsub();
      });
    };
  }, [startHeartbeat, stopHeartbeat]);

  // Set up WebSocket event listeners
  useEffect(() => {
    const unsubscribers: Array<() => void> = [];

    // Message event
    unsubscribers.push(
      subscribeToEvent('message', (data) => {
        callbacksRef.current.onMessage?.(data);
      })
    );

    // Status event
    unsubscribers.push(
      subscribeToEvent('status', (data) => {
        callbacksRef.current.onStatus?.(data);
      })
    );

    // Replace event
    unsubscribers.push(
      subscribeToEvent('replace', (data) => {
        callbacksRef.current.onReplace?.(data);
      })
    );

    // Source event
    unsubscribers.push(
      subscribeToEvent('source', (data) => {
        callbacksRef.current.onSource?.(data);
      })
    );

    // Citation event
    unsubscribers.push(
      subscribeToEvent('citation', (data) => {
        callbacksRef.current.onCitation?.(data);
      })
    );

    // File processing event
    unsubscribers.push(
      subscribeToEvent('file-processing', (data) => {
        callbacksRef.current.onFileProcessing?.(data);
      })
    );

    // Knowledge document processing event
    unsubscribers.push(
      subscribeToEvent('knowledge-processing', (data) => {
        callbacksRef.current.onKnowledgeProcessing?.(data);
      })
    );

    // Chat title updated event
    unsubscribers.push(
      subscribeToEvent('chat_title_updated', (data) => {
        callbacksRef.current.onChatTitleUpdated?.(data);
      })
    );

    // Chat created event (e.g. voice sessions)
    unsubscribers.push(
      subscribeToEvent('chat_created', (data) => {
        callbacksRef.current.onChatCreated?.(data);
      })
    );

    // Per-chat streaming progress event (relayed from SSE; survives PWA wake)
    unsubscribers.push(
      subscribeToEvent('chat_progress', (data) => {
        callbacksRef.current.onChatProgress?.(data);
      })
    );

    // The user's Andi look changed (edit succeeded or reset)
    unsubscribers.push(
      subscribeToEvent('avatar_updated', (data) => {
        callbacksRef.current.onAvatarUpdated?.(data);
      })
    );

    return () => {
      unsubscribers.forEach((unsub) => {
        unsub();
      });
    };
  }, []);

  // Auto-connect on mount. The connection is shared and reference counted
  // (acquireConnection), so a duplicate or StrictMode mount reuses the one
  // socket, and letting go closes it once nobody holds it (logout, unmount).
  // Connect failures are retried by the socket module's backoff and surface
  // through the connection events, so there is nothing to catch here.
  useEffect(() => {
    if (!autoConnect) return;
    const release = acquireConnection();
    // A remount can land while the socket is still open: no `connect` event
    // will come, so the heartbeat would never restart without this.
    if (isConnected()) startHeartbeat();
    return () => {
      release();
      stopHeartbeat();
    };
  }, [autoConnect, startHeartbeat, stopHeartbeat]);

  return {
    connectionState,
    isConnected: socketIsConnected,
    connect: handleConnect,
    disconnect: handleDisconnect,
    error,
  };
}

/**
 * Hook for subscribing to specific WebSocket events without managing connection.
 * Useful when connection is managed by a parent component.
 *
 * @param callbacks - Event callbacks
 *
 * @example
 * ```tsx
 * function FileListComponent() {
 *   const [fileStatuses, setFileStatuses] = useState<Map<string, FileProcessingStatus>>(new Map());
 *
 *   useWebSocketEvents({
 *     onFileProcessing: (data) => {
 *       setFileStatuses(prev => new Map(prev).set(data.file_id, data.status));
 *     },
 *   });
 *
 *   // Render file list with statuses
 * }
 * ```
 */
export function useWebSocketEvents(callbacks: WebSocketEventCallbacks): void {
  const {
    onMessage,
    onStatus,
    onReplace,
    onSource,
    onCitation,
    onFileProcessing,
    onKnowledgeProcessing,
    onChatTitleUpdated,
    onChatCreated,
    onChatProgress,
    onAvatarUpdated,
  } = callbacks;

  // Refs for callbacks
  const callbacksRef = useRef({
    onMessage,
    onStatus,
    onReplace,
    onSource,
    onCitation,
    onFileProcessing,
    onKnowledgeProcessing,
    onChatTitleUpdated,
    onChatCreated,
    onChatProgress,
    onAvatarUpdated,
  });

  // Update refs when callbacks change
  useEffect(() => {
    callbacksRef.current = {
      onMessage,
      onStatus,
      onReplace,
      onSource,
      onCitation,
      onFileProcessing,
      onKnowledgeProcessing,
      onChatTitleUpdated,
      onChatCreated,
      onChatProgress,
      onAvatarUpdated,
    };
  }, [
    onMessage,
    onStatus,
    onReplace,
    onSource,
    onCitation,
    onFileProcessing,
    onKnowledgeProcessing,
    onChatTitleUpdated,
    onChatCreated,
    onChatProgress,
    onAvatarUpdated,
  ]);

  // Set up event subscriptions
  useEffect(() => {
    const unsubscribers: Array<() => void> = [];

    if (callbacksRef.current.onMessage) {
      unsubscribers.push(
        subscribeToEvent('message', (data) => {
          callbacksRef.current.onMessage?.(data);
        })
      );
    }

    if (callbacksRef.current.onStatus) {
      unsubscribers.push(
        subscribeToEvent('status', (data) => {
          callbacksRef.current.onStatus?.(data);
        })
      );
    }

    if (callbacksRef.current.onReplace) {
      unsubscribers.push(
        subscribeToEvent('replace', (data) => {
          callbacksRef.current.onReplace?.(data);
        })
      );
    }

    if (callbacksRef.current.onSource) {
      unsubscribers.push(
        subscribeToEvent('source', (data) => {
          callbacksRef.current.onSource?.(data);
        })
      );
    }

    if (callbacksRef.current.onCitation) {
      unsubscribers.push(
        subscribeToEvent('citation', (data) => {
          callbacksRef.current.onCitation?.(data);
        })
      );
    }

    if (callbacksRef.current.onFileProcessing) {
      unsubscribers.push(
        subscribeToEvent('file-processing', (data) => {
          callbacksRef.current.onFileProcessing?.(data);
        })
      );
    }

    if (callbacksRef.current.onKnowledgeProcessing) {
      unsubscribers.push(
        subscribeToEvent('knowledge-processing', (data) => {
          callbacksRef.current.onKnowledgeProcessing?.(data);
        })
      );
    }

    if (callbacksRef.current.onChatTitleUpdated) {
      unsubscribers.push(
        subscribeToEvent('chat_title_updated', (data) => {
          callbacksRef.current.onChatTitleUpdated?.(data);
        })
      );
    }

    if (callbacksRef.current.onChatCreated) {
      unsubscribers.push(
        subscribeToEvent('chat_created', (data) => {
          callbacksRef.current.onChatCreated?.(data);
        })
      );
    }

    if (callbacksRef.current.onChatProgress) {
      unsubscribers.push(
        subscribeToEvent('chat_progress', (data) => {
          callbacksRef.current.onChatProgress?.(data);
        })
      );
    }

    if (callbacksRef.current.onAvatarUpdated) {
      unsubscribers.push(
        subscribeToEvent('avatar_updated', (data) => {
          callbacksRef.current.onAvatarUpdated?.(data);
        })
      );
    }

    return () => {
      unsubscribers.forEach((unsub) => {
        unsub();
      });
    };
  }, []);
}

/**
 * Export socket management functions for direct use.
 */
export { cleanupSocket };
